import { QuestionField } from "#batteries-included-components/Forms/FormSubmissionForm/FormSubmissionSections/FormSubmissionField/QuestionField";
import {
  useGenerateTemplatedReport,
  useGetOneTemplatedReport,
} from "#hooks/adapters/useTemplatedReports";
import { useNavigate, useParams } from "#routers/hooks";
import {
  TemplatedReportExportParamType,
  TemplatedReportsExportRoutePath,
} from "#src/routes/reports/templated-reports/template/[templateName]/export/index";
import { TemplatedReportsListRoutePath } from "#routes/reports/templated-reports/index";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  Button,
  Form,
  Page,
  Panel,
  useForm,
} from "@validereinc/common-components";
import React from "react";

/** Don't submit empty values */
const sanitizeValues = (inputs: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(inputs).filter(([_, value]) => !!value && value !== "")
  );

export const TemplatedReportExportPage = () => {
  const navigate = useNavigate();
  const { reportName } = useParams<TemplatedReportExportParamType>();
  const { data, isLoading } = useGetOneTemplatedReport({ id: reportName });
  const [breadcrumbs, breadcrumbsIsLoading] = useBreadcrumbsFromRoute(
    TemplatedReportsExportRoutePath,
    {
      "/:reportName": {
        title: data?.data?.display_name,
      },
    }
  );

  const navigateToTemplatedReportList = () => {
    navigate(TemplatedReportsListRoutePath.toLinkParts());
  };
  const generateReport = useGenerateTemplatedReport({
    onSuccess: navigateToTemplatedReportList,
  });

  const form = useForm();

  const handleExport = form.handleSubmit((values) => {
    generateReport.mutate({
      templatedReportName: reportName,
      name: data?.data?.display_name ?? "",
      input: sanitizeValues(values),
    });
  });

  const inputs = Object.entries(data?.data?.input_schema ?? {}).map(
    ([key, values]) => ({ key, ...values })
  );
  const displayName = data?.data?.display_name
    ? `Export ${data?.data?.display_name}`
    : TemplatedReportsExportRoutePath.title;

  return (
    <Page
      title={displayName}
      category={TemplatedReportsExportRoutePath.previous?.title}
      breadcrumbs={breadcrumbs}
      isLoading={isLoading || breadcrumbsIsLoading}
      footer={
        <>
          <Button
            onClick={() => navigateToTemplatedReportList()}
            disabled={generateReport.isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            type="submit"
            onClick={handleExport}
            disabled={isLoading || generateReport.isLoading}
          >
            Export
          </Button>
        </>
      }
    >
      <Panel title="Report Details">
        <Form {...form}>
          To create a templated report, configure the fields below and click
          &quot;Export.&quot;
          <div style={{ margin: 8, width: 254 }}>
            {inputs.map((input) => (
              <QuestionField
                {...input}
                key={input.key}
                name={input.key}
                prompt={input.display_name}
              />
            ))}
          </div>
        </Form>
      </Panel>
    </Page>
  );
};
